<script>
import Modal from '@components/Shared/UhModal';
import UhNumberStepper from '@components/Shared/Inputs/UhNumberStepper.vue';
import UniButton from '@components/Shared/UniButton.vue';

export default {
  components: {
    UniButton,
    UhNumberStepper,
    Modal,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    roomCount: {
      type: Number,
      required: false,
      default: 0,
    },
    bathroomCount: {
      type: Number,
      required: false,
      default: 0,
    },
    currentMaxPrice: {
      type: Number,
      required: false,
      default: 0,
    },
    useExactRoomCounts: {
      type: Boolean,
      default: false,
    },
    currentEnquiredProperties: {
      type: Boolean,
      default: false,
    },
    enableStudentEnquiriesCta: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    maxPrice: 0,
  }),
  computed: {
    displaySymbol() {
      if (this.useExactRoomCounts) {
        return '';
      }

      return '+';
    },

    maxBedroomCount() {
      if (this.useExactRoomCounts || !this.roomCount) {
        return;
      }

      return this.roomCount;
    },
  },
  watch: {
    maxPrice: {
      handler(newValue) {
        this.$emit('set-max-price', newValue);
      },
    },
  },
  mounted() {
    this.maxPrice = this.$props.currentMaxPrice;
  },
  methods: {
    handleBedroomChange(newValue) {
      if (!this.useExactRoomCounts && this.bathroomCount > newValue) {
        this.handleBathroomChange(newValue);
      }

      // returning null instead of 0 for backwards compatibility
      this.$emit('set-current-bedrooms', newValue || null);
    },
    handleBathroomChange(newValue) {
      // returning null instead of 0 for backwards compatibility
      this.$emit('set-current-bathrooms', newValue || null);
    },
    handleClear() {
      this.maxPrice = 0;
      this.$emit('set-current-bedrooms', null);
      this.$emit('set-current-bathrooms', null);
      this.$emit('set-max-price', null);
      this.$emit('close');
    },
    handleClose() {
      this.$emit('close');
    },
    handleNaN(value) {
      return isNaN(value) ? 0 : value;
    },
  },
};
</script>

<template>
  <Modal
    :show="show"
    modal-classes="tw-rounded-2xl"
    footer-classes="tw-border-t tw-border-[#eeeeee] tw-p-[24px]"
    width="375px"
    dusk="search-filter-modal"
    @close="handleClose"
  >
    <template #header>
      <span class="tw-font-semibold tw-text-primary-grey">
        Rooms and price
      </span>
    </template>

    <template #body>
      <div class="tw-relative">
        <div
          v-if="currentEnquiredProperties && enableStudentEnquiriesCta"
          class="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-h-full tw-w-full tw-bg-white/80"
        ></div>
        <p class="tw-text-base tw-font-semibold tw-leading-6">Rooms</p>
        <div class="tw-mb-5 tw-flex tw-flex-row tw-justify-between">
          <UhNumberStepper
            :existing-value="handleNaN(roomCount)"
            :max="15"
            default-value="Any"
            dusk="bedrooms"
            @update="handleBedroomChange"
          >
            <template #label>
              <img
                :src="cdnAsset('svg/uh-bed-icon.svg')"
                alt="Location Pin"
                class="tw-mr-2 tw-h-4 tw-w-4"
              />Bedrooms
            </template>
          </UhNumberStepper>
          <UhNumberStepper
            :existing-value="handleNaN(bathroomCount)"
            default-value="Any"
            :display-symbol="displaySymbol"
            :max="maxBedroomCount"
            dusk="bathrooms"
            test-id="stepper-bathrooms"
            @update="handleBathroomChange"
          >
            <template #label>
              <img
                :src="cdnAsset('svg/listings/shower.svg')"
                alt="Shower and Bathroom icon"
                class="tw-mr-2 tw-h-4 tw-w-4"
              />
              Bathrooms
            </template>
          </UhNumberStepper>
        </div>
      </div>

      <div class="tw-mb-5 tw-w-full tw-border tw-border-gray-100" />

      <div class="tw-relative">
        <div
          v-if="currentEnquiredProperties && enableStudentEnquiriesCta"
          class="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-h-full tw-w-full tw-bg-white/80"
        ></div>
        <p class="tw-test-base tw-mb-0 tw-font-semibold tw-leading-6">
          Price range (per person per week)
        </p>
        <div
          class="tw-flex tw-flex-row tw-items-baseline tw-justify-between tw-gap-4"
        >
          <div>
            <img
              :src="cdnAsset('svg/listings/sterling.svg')"
              alt="Pound Sign"
              class="tw-relative tw-left-[15px] tw-top-[60px] tw-z-1 tw-h-4 tw-w-4"
            />
            <p class="tw-text-sm tw-font-normal">
              Max price
              <input
                id="max-price"
                v-model="maxPrice"
                class="tw-mt-1 tw-w-full tw-rounded-md tw-border tw-border-gray-100 tw-pl-8 focus:tw-border-2 focus:tw-border-[#00A2E1] focus:tw-duration-100"
                type="number"
                dusk="search-filter-modal-max-price"
                placeholder="Enter your maximum pppw"
              />
            </p>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="tw-inline-flex tw-items-center tw-justify-between">
        <span
          class="px-0 tw-ease tw-relative tw-right-auto tw-top-0 tw-flex tw-w-[158px] tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-[#5C6A75] tw-bg-white tw-px-[20px] tw-py-[10px] tw-text-center tw-text-[16px] tw-font-semibold tw-leading-[32px] tw-text-[#5C6A75] tw-transition-all tw-duration-300 hover:tw-cursor-pointer hover:tw-border-[#00A2E1] hover:tw-bg-[#d9e4e9] focus:tw-border-[#00A2E1]"
          dusk="search-filter-modal-clear-search"
          @click="handleClear"
          >Clear</span
        >
        <span
          class="px-0 tw-ease tw-relative tw-right-auto tw-top-0 tw-flex tw-w-[158px] tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-bg-orange-500 tw-px-[20px] tw-py-[10px] tw-text-center tw-text-[16px] tw-font-semibold tw-leading-[32px] tw-text-white tw-transition-all tw-duration-300 hover:tw-cursor-pointer hover:tw-bg-refreshPrimaryBg-dark"
          dusk="search-filter-modal-show-results"
          @click="handleClose"
          >Show results</span
        >
      </div>
    </template>
  </Modal>
</template>
